import {Button, CircularProgress} from "@mui/material";
import React, {useCallback, useRef, useState} from "react";

import {acquireSharePointToken} from "../hooks/useEntraAuthentication";

const PICKER_BASE_URL = process.env.REACT_APP_RESOURCE_BASE_PATH;
const PICKER_PATH = "/_layouts/15/FilePicker.aspx";
const PICKER_WINDOW_WIDTH = 1080;
const PICKER_WINDOW_HEIGHT = 680;
const FILE_PICKER_SDK_VERSION = process.env.REACT_APP_ONEDRIVE_FILE_PICKER_SDK_VERSION;

const buildPickerOptions = (channelId) => ({
    sdk: FILE_PICKER_SDK_VERSION,
    entry: {
        sharePoint: {},
    },
    authentication: {},
    messaging: {
        origin: window.location.origin,
        channelId,
    },
    selection: {
        mode: "single",
    },
    commands: {
        pick: {
            action: "share",
        },
    },
});

function OneDriveFilePicker(props) {
    const {filePickerButtonLabel, handleChosenFile} = props;
    const [isLoading, setIsLoading] = useState(false);
    const popupRef = useRef(null);
    const portRef = useRef(null);

    const handleAuthenticate = useCallback(async (messageData) => {
        try {
            const token = await acquireSharePointToken(messageData.data.resource);
            portRef.current?.postMessage({
                type: "result",
                id: messageData.id,
                data: {
                    result: "token",
                    token,
                },
            });
        } catch (error) {
            portRef.current?.postMessage({
                type: "result",
                id: messageData.id,
                data: {
                    result: "error",
                    error: {
                        code: "unableToObtainToken",
                        message: error.message,
                    },
                },
            });
        }
    }, []);

    const handlePick = useCallback((messageData) => {
        portRef.current?.postMessage({
            type: "result",
            id: messageData.id,
            data: {result: "success"},
        });

        const items = messageData.data?.items;
        if (items?.length > 0) {
            handleChosenFile(items[0]);
        }

        popupRef.current?.close();
        setIsLoading(false);
    }, [handleChosenFile]);

    const handleClose = useCallback((messageData) => {
        portRef.current?.postMessage({
            type: "result",
            id: messageData.id,
            data: {result: "success"},
        });

        popupRef.current?.close();
        setIsLoading(false);
    }, []);

    const handleChannelMessage = useCallback(async (message) => {
        const payload = message.data;

        switch (payload.type) {
            case "notification":
                break;

            case "command":
                portRef.current?.postMessage({
                    type: "acknowledge",
                    id: payload.id,
                });

                switch (payload.data?.command) {
                    case "authenticate":
                        await handleAuthenticate(payload);
                        break;
                    case "pick":
                        handlePick(payload);
                        break;
                    case "close":
                        handleClose(payload);
                        break;
                    default:
                        portRef.current?.postMessage({
                            type: "result",
                            id: payload.id,
                            data: {
                                result: "error",
                                error: {
                                    code: "unsupportedCommand",
                                    message: payload.data?.command,
                                },
                            },
                        });
                        break;
                }
                break;

            default:
                break;
        }
    }, [handleAuthenticate, handlePick, handleClose]);

    const launchPicker = useCallback(async () => {
        setIsLoading(true);

        try {
            const accessToken = await acquireSharePointToken(PICKER_BASE_URL);
            const channelId = crypto.randomUUID();
            const options = buildPickerOptions(channelId);

            const left = window.screenX + Math.max(0, Math.floor((window.outerWidth - PICKER_WINDOW_WIDTH) / 2));
            const top = window.screenY + Math.max(0, Math.floor((window.outerHeight - PICKER_WINDOW_HEIGHT) / 2));
            const pickerWindowFeatures = `width=${PICKER_WINDOW_WIDTH},height=${PICKER_WINDOW_HEIGHT},left=${left},top=${top}`;

            const popup = window.open("", "OneDrivePicker", pickerWindowFeatures);
            if (!popup) {
                setIsLoading(false);
                return;
            }
            popupRef.current = popup;

            const onInitialize = (event) => {
                if (event.source !== popup) {
                    return;
                }

                const msg = event.data;
                if (msg.type === "initialize" && msg.channelId === channelId) {
                    portRef.current = event.ports[0];
                    portRef.current.addEventListener("message", handleChannelMessage);
                    portRef.current.start();
                    portRef.current.postMessage({type: "activate"});
                }
            };

            window.addEventListener("message", onInitialize);

            const closedCheck = setInterval(() => {
                if (popup.closed) {
                    clearInterval(closedCheck);
                    window.removeEventListener("message", onInitialize);
                    setIsLoading(false);
                }
            }, 500);

            const queryString = new URLSearchParams({
                filePicker: JSON.stringify(options),
                locale: "en-us",
            });

            const url = `${PICKER_BASE_URL}${PICKER_PATH}?${queryString}`;

            const form = popup.document.createElement("form");
            form.setAttribute("action", url);
            form.setAttribute("method", "POST");

            const tokenInput = popup.document.createElement("input");
            tokenInput.setAttribute("type", "hidden");
            tokenInput.setAttribute("name", "access_token");
            tokenInput.setAttribute("value", accessToken);
            form.appendChild(tokenInput);

            popup.document.body.append(form);
            form.submit();
        } catch (error) {
            console.error("Failed to launch file picker:", error);
            setIsLoading(false);
        }
    }, [handleChannelMessage]);

    return (
        <Button
            id='select-file-btn'
            variant='contained'
            disabled={isLoading}
            onClick={launchPicker}
            style={{width: '9.375rem'}}>
            {
                isLoading
                ?
                <CircularProgress size="1.5rem" color="inherit"/>
                :
                filePickerButtonLabel
            }
        </Button>
    );
}

export default OneDriveFilePicker;
